import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { Navbar } from "../components/Navbar";
import { UserAvatar, EfsaneBadge, VerifiedBadge } from "../components/UserAvatar";
import { toast } from "sonner";
import { MessageCircle, Camera, Check, Flame, Wallet, ArrowDownToLine } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

export default function Profile() {
  const { username } = useParams();
  const { user, setUser } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [bio, setBio] = useState("");
  const [name, setName] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [listType, setListType] = useState("followers");
  const [listUsers, setListUsers] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [topup, setTopup] = useState(100);
  const [wIban, setWIban] = useState("");
  const [wAmount, setWAmount] = useState("");
  const fileRef = useRef();

  const loadWallet = async () => {
    try { const { data } = await api.get("/wallet"); setWallet(data); } catch {}
  };
  useEffect(() => {
    if (user && username === user.username) loadWallet();
    else setWallet(null);
  }, [user, username]);

  const buyCoins = async () => {
    const coins = parseInt(topup, 10);
    if (!coins || coins < 10) { toast.error("En az 10 eCoin yükleyebilirsiniz"); return; }
    try { const { data } = await api.post("/wallet/topup", { coins }); window.location.href = data.payment_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const withdraw = async () => {
    const amount = parseInt(wAmount, 10);
    if (!amount) { toast.error("Tutar girin"); return; }
    try {
      await api.post("/wallet/withdraw", { iban: wIban, amount });
      toast.success("Çekim talebiniz alındı");
      setWIban(""); setWAmount(""); loadWallet();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${username}`);
      setProfile(data);
      setBio(data.bio || "");
      setName(data.name || "");
    } catch {
      toast.error("Kullanıcı bulunamadı");
      navigate("/");
    }
  };

  useEffect(() => { load(); }, [username]);

  const toggleFollow = async () => {
    try {
      const { data } = await api.post(`/users/${username}/follow`);
      setProfile((p) => ({
        ...p, is_following: data.following,
        follower_count: p.follower_count + (data.following ? 1 : -1),
      }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/users/me", { bio, name });
      setUser(data);
      setEditOpen(false);
      toast.success("Profil güncellendi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser((u) => ({ ...u, avatar_url: data.avatar_url }));
      toast.success("Profil fotoğrafı güncellendi");
      load();
    } catch (e) { toast.error("Yükleme başarısız"); }
  };

  const openList = async (type) => {
    setListType(type);
    setListUsers([]);
    setListOpen(true);
    try {
      const { data } = await api.get(`/users/${username}/${type}`);
      setListUsers(data);
    } catch {}
  };

  const buyPremium = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/premium/checkout");
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBuying(false);
    }
  };

  if (!profile) return <div className="min-h-screen bg-[#141414]"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-[#141414]">
      <Navbar />
      <div className="relative h-52 sm:h-64 w-full overflow-hidden">
        <img src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="banner" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-5">
          <div className="relative">
            <UserAvatar user={profile} premium={profile.is_premium} className="h-32 w-32 ring-4 ring-[#141414] shadow-2xl" />
            {profile.is_me && (
              <>
                <button data-testid="avatar-upload-button" onClick={() => fileRef.current?.click()}
                  className="absolute bottom-1 right-1 bg-[#E50914] hover:bg-[#F40612] p-2 rounded-full text-white transition z-10">
                  <Camera size={16} />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
              </>
            )}
          </div>
          <div className="flex-1 pb-2">
            <h1 className="text-3xl font-black text-white flex items-center gap-2 flex-wrap">{profile.name} {profile.verified && <VerifiedBadge size={22} />} {profile.is_premium && <EfsaneBadge className="text-lg" />}</h1>
            <p className="text-gray-400">@{profile.username}</p>
          </div>
          <div className="flex gap-3 pb-2">
            {profile.is_me ? (
              <>
                {settings.premium_enabled && !profile.is_premium && (
                  <button data-testid="efsane-ol-button" onClick={() => setPremiumOpen(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-[#ff8a00] to-[#E50914] hover:opacity-90 text-white font-semibold px-5 py-2.5 rounded-md transition active:scale-95 shadow-[0_0_18px_rgba(255,90,0,.5)]">
                    <Flame size={16} /> Efsane Ol
                  </button>
                )}
                {profile.is_premium && (
                  <span className="flex items-center gap-2 bg-white/5 border border-orange-500/40 px-4 py-2.5 rounded-md"><EfsaneBadge /> <span className="text-xs text-gray-400">Premium Üye</span></span>
                )}
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild>
                  <button data-testid="edit-profile-button" className="bg-white/10 hover:bg-white/20 text-white font-medium px-5 py-2.5 rounded-md transition">Profili Düzenle</button>
                </DialogTrigger>
                <DialogContent className="bg-[#1E1E1E] border-white/10 text-white">
                  <DialogHeader><DialogTitle>Profili Düzenle</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Ad Soyad</label>
                      <input data-testid="edit-name-input" value={name} onChange={(e) => setName(e.target.value)}
                        className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914]" />
                    </div>
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Biyografi</label>
                      <textarea data-testid="edit-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} rows={3}
                        className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-4 py-2.5 focus:outline-none focus:border-[#E50914] resize-none" />
                    </div>
                    <button data-testid="save-profile-button" onClick={saveProfile} className="w-full bg-[#E50914] hover:bg-[#F40612] text-white font-semibold py-2.5 rounded-md transition">Kaydet</button>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            ) : user ? (
              <>
                <button data-testid="follow-button" onClick={toggleFollow}
                  className={`font-medium px-5 py-2.5 rounded-md transition flex items-center gap-2 ${profile.is_following ? "bg-white/10 hover:bg-white/20 text-white" : "bg-[#E50914] hover:bg-[#F40612] text-white"}`}>
                  {profile.is_following ? <><Check size={16} /> Takip Ediliyor</> : "Takip Et"}
                </button>
                <button data-testid="message-button" onClick={() => navigate(`/messages/${profile.username}`)}
                  className="bg-white/10 hover:bg-white/20 text-white font-medium px-5 py-2.5 rounded-md transition flex items-center gap-2">
                  <MessageCircle size={16} /> Mesaj
                </button>
              </>
            ) : null}
          </div>
        </div>

        <p className="mt-5 text-gray-300 max-w-2xl">{profile.bio || "Henüz biyografi eklenmemiş."}</p>

        <div className="mt-5 flex gap-8">
          <button data-testid="followers-count" onClick={() => openList("followers")} className="hover:opacity-80 transition">
            <span className="text-xl font-bold text-white">{profile.follower_count}</span> <span className="text-gray-400 text-sm">Takipçi</span>
          </button>
          <button data-testid="following-count" onClick={() => openList("following")} className="hover:opacity-80 transition">
            <span className="text-xl font-bold text-white">{profile.following_count}</span> <span className="text-gray-400 text-sm">Takip</span>
          </button>
        </div>

        {profile.is_me && wallet && (
          <div data-testid="ecoin-wallet" className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#181818] border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><img src="/ecoin.png" alt="eCoin" className="h-7 w-7" /><span className="text-white font-bold">eCoin Cüzdanı</span></div>
              <div className="flex items-end gap-1.5 mb-4">
                <span data-testid="coin-balance" className="text-4xl font-black text-white">{wallet.coin_balance}</span>
                <span className="text-gray-400 mb-1">eCoin</span>
              </div>
              <label className="text-xs text-gray-400 mb-1.5 block">eCoin Yükle (1 eCoin = 1₺)</label>
              <div className="flex gap-2">
                <input data-testid="topup-input" type="number" min="10" value={topup} onChange={(e) => setTopup(e.target.value)}
                  className="flex-1 min-w-0 bg-[#2B2B2B] border border-white/10 text-white rounded-md px-3 py-2 focus:outline-none focus:border-[#E50914]" />
                <button data-testid="topup-button" onClick={buyCoins} className="flex items-center gap-1.5 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-4 rounded-md whitespace-nowrap">
                  <Wallet size={16} /> Satın Al
                </button>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">Ödeme Shopier güvenli sayfasında yapılır.</p>
            </div>

            {(wallet.is_broadcaster || wallet.earned_balance > 0) && (
              <div className="bg-[#181818] border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3"><ArrowDownToLine size={18} className="text-green-400" /><span className="text-white font-bold">Kazanç & Para Çekme</span></div>
                <div className="flex items-end gap-1.5 mb-1">
                  <span data-testid="earned-balance" className="text-4xl font-black text-green-400">{wallet.earned_balance}</span>
                  <span className="text-gray-400 mb-1">₺</span>
                </div>
                <p className="text-[11px] text-gray-500 mb-3">Minimum çekim {wallet.min_withdrawal}₺ • Kazanç oranı %{wallet.earn_rate}</p>
                <input data-testid="withdraw-iban" value={wIban} onChange={(e) => setWIban(e.target.value)} placeholder="IBAN (TR...)"
                  className="w-full bg-[#2B2B2B] border border-white/10 text-white rounded-md px-3 py-2 mb-2 font-mono text-sm focus:outline-none focus:border-[#E50914]" />
                <div className="flex gap-2">
                  <input data-testid="withdraw-amount" type="number" value={wAmount} onChange={(e) => setWAmount(e.target.value)} placeholder={`Tutar (₺)`}
                    className="flex-1 min-w-0 bg-[#2B2B2B] border border-white/10 text-white rounded-md px-3 py-2 focus:outline-none focus:border-[#E50914]" />
                  <button data-testid="withdraw-button" onClick={withdraw} className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 rounded-md whitespace-nowrap">Çek</button>
                </div>
              </div>
            )}
          </div>
        )}

        <Dialog open={listOpen} onOpenChange={setListOpen}>
          <DialogContent className="bg-[#1E1E1E] border-white/10 text-white max-w-md">
            <DialogHeader><DialogTitle>{listType === "followers" ? "Takipçiler" : "Takip Edilenler"}</DialogTitle></DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto no-scrollbar -mx-2">
              {listUsers.length === 0 && <p className="text-sm text-gray-500 px-4 py-6 text-center">Liste boş.</p>}
              {listUsers.map((u) => (
                <button key={u.id} data-testid={`list-user-${u.username}`} onClick={() => { setListOpen(false); navigate(`/u/${u.username}`); }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 hover:bg-white/5 text-left rounded-lg">
                  <UserAvatar user={u} className="h-10 w-10" />
                  <div><div className="text-sm text-white">@{u.username}</div><div className="text-xs text-gray-500">{u.name}</div></div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={premiumOpen} onOpenChange={setPremiumOpen}>
          <DialogContent className="bg-[#161616] border-orange-500/30 text-white max-w-md overflow-hidden">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at top, rgba(255,90,0,.35), transparent 60%)" }} />
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-2xl"><Flame className="text-orange-500" /> Efsane Ol</DialogTitle></DialogHeader>
            <div className="relative space-y-3 pt-2">
              <p className="text-gray-300 text-sm">Premium ol, tüm efsane ayrıcalıklarını aç:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check size={16} className="text-orange-500" /> Alev animasyonlu profil çerçevesi</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-orange-500" /> Adının yanında animasyonlu <EfsaneBadge /> rozeti</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-orange-500" /> Sohbette özel animasyonlu mesaj balonu</li>
                <li className="flex items-center gap-2"><Check size={16} className="text-orange-500" /> Premium'a özel emoji/çıkartma paketi</li>
              </ul>
              <div className="text-center py-2">
                <span className="text-4xl font-black text-white">{settings.premium_price}₺</span>
                <span className="text-gray-400"> / aylık</span>
              </div>
              <button data-testid="premium-buy-button" onClick={buyPremium} disabled={buying}
                className="w-full bg-gradient-to-r from-[#ff8a00] to-[#E50914] hover:opacity-90 text-white font-bold py-3 rounded-md transition active:scale-95 disabled:opacity-60">
                {buying ? "Yönlendiriliyor..." : "Shopier ile Güvenli Öde"}
              </button>
              <p className="text-center text-[11px] text-gray-500">Ödeme Shopier güvenli sayfasında yapılır. Ödeme sonrası premium otomatik aktif olur.</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
